import { Badge, Box, Button, chakra, Text } from '@chakra-ui/react';
import moment from 'moment';
import AppCard from 'presentation/components/shared/card/AppCard';
import CardInfoRow from 'presentation/components/shared/card/CardInfoRow';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatSelfIpoDateTime,
  formatSelfIpoMoneyAmount,
  formatSelfIpoPercent,
  getSelfIpoApplicationStatusText,
} from './selfIpoUtils';

interface Props {
  application: SelfIpoApplication;
  onClick?: () => void;
  onCancel?: () => void;
}

const SelfIpoApplicationCard: FC<Props> = ({
  application,
  onClick,
  onCancel,
  ...props
}) => {
  const { i18n, t } = useTranslation();
  const isClickable = Boolean(onClick && application.offering);

  return (
    <AppCard
      overflow="hidden"
      cursor={isClickable ? 'pointer' : undefined}
      onClick={() => {
        if (isClickable && onClick) {
          onClick();
        }
      }}
      display="flex"
      flexDir="column"
      h="100%"
      p="20px"
      {...props}
    >
      <Box mb="10px">
        <Text fontWeight={700} fontSize="22px" noOfLines={2}>
          {application.offering
            ? `${application.offering.tickerCode} - ${application.offering.companyName}`
            : `#${application.offeringId}`}
        </Text>
        <Badge mt="8px">
          {getSelfIpoApplicationStatusText(t, application.status)}
        </Badge>
      </Box>
      <CardInfoRow label={t('selfIpoAmount')}>
        <Text>${formatSelfIpoMoneyAmount(application.amount)}</Text>
      </CardInfoRow>
      <CardInfoRow label={t('selfIpoSubmittedAt')}>
        <Text>
          {formatSelfIpoDateTime(application.submittedAt, i18n.language)}
        </Text>
      </CardInfoRow>
      {application.allocationPercent !== null && (
        <CardInfoRow label={t('selfIpoAllocationPercent')}>
          <Text>{application.allocationPercent}%</Text>
        </CardInfoRow>
      )}
      {application.allocatedAmount !== null && (
        <CardInfoRow label={t('selfIpoAllocatedAmount')}>
          <Text>${formatSelfIpoMoneyAmount(application.allocatedAmount)}</Text>
        </CardInfoRow>
      )}
      {application.buyPrice !== null && (
        <CardInfoRow label={t('selfIpoBuyPrice')}>
          <Text>${formatSelfIpoMoneyAmount(application.buyPrice)}</Text>
        </CardInfoRow>
      )}
      {application.buyQuantity !== null && (
        <CardInfoRow label={t('selfIpoBuyQuantity')}>
          <Text>{application.buyQuantity}</Text>
        </CardInfoRow>
      )}
      {application.buyDate !== null && (
        <CardInfoRow label={t('selfIpoBuyDate')}>
          <Text>{moment(application.buyDate).format('L')}</Text>
        </CardInfoRow>
      )}
      {application.unlockDate !== null && (
        <CardInfoRow label={t('selfIpoUnlockDate')}>
          <Text>{moment(application.unlockDate).format('L')}</Text>
        </CardInfoRow>
      )}
      {application.profitPercent !== null && (
        <CardInfoRow label={t('selfIpoProfitPercent')}>
          <Text>{formatSelfIpoPercent(application.profitPercent)}%</Text>
        </CardInfoRow>
      )}
      {application.fixedProfitPercent !== null && (
        <CardInfoRow label={t('selfIpoFixedProfitPercent')}>
          <Text>{formatSelfIpoPercent(application.fixedProfitPercent)}%</Text>
        </CardInfoRow>
      )}
      <CardInfoRow label={t('selfIpoDealFee')}>
        <Text>{application.dealFee}%</Text>
      </CardInfoRow>
      <CardInfoRow label={t('selfIpoSuccessFee')}>
        <Text>{application.successFee}%</Text>
      </CardInfoRow>
      <CardInfoRow label={t('selfIpoLockUpDays')} showBorder={false}>
        <Text>{application.lockUp}</Text>
      </CardInfoRow>
      {application.canCancel && onCancel && (
        <Box mt="auto" pt="15px">
          <Button
            w="100%"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          >
            {t('cancelAction')}
          </Button>
        </Box>
      )}
    </AppCard>
  );
};

export default chakra(SelfIpoApplicationCard);
